/**
 * One `message` listener per socket, parsing each frame once and handing the
 * object to every subscriber.
 *
 * Live grid cells and option-chain rows used to add their own listener each and
 * `JSON.parse` every frame: a 100-row grid with two live columns ran ~200 parses
 * per tick, and an options chain one per strike. Subscribers still decide
 * whether a frame is theirs, but the parse happens once per socket and frame.
 * The socket's listener is added with the first subscriber and removed with the
 * last, so a replaced socket (`useAppWebSockets` reconnects by replacing it)
 * leaves nothing behind.
 */
type Subscriber = (parsed: any) => void;

const registry = new WeakMap<WebSocket, { listener: (event: MessageEvent) => void; subscribers: Set<Subscriber> }>();

export const onParsedMessage = (ws: WebSocket, subscriber: Subscriber): (() => void) => {
    let entry = registry.get(ws);
    if (!entry) {
        const subscribers = new Set<Subscriber>();
        const listener = ({ data }: MessageEvent) => {
            let parsed: any;
            try {
                parsed = JSON.parse(data);
            } catch {
                return;
            }
            subscribers.forEach((fn) => {
                try {
                    fn(parsed);
                } catch (error) {
                    console.error("Error with WS ", error);
                }
            });
        };
        ws.addEventListener("message", listener);
        entry = { listener, subscribers };
        registry.set(ws, entry);
    }
    entry.subscribers.add(subscriber);
    return () => {
        const current = registry.get(ws);
        if (!current) return;
        current.subscribers.delete(subscriber);
        if (current.subscribers.size === 0) {
            ws.removeEventListener("message", current.listener);
            registry.delete(ws);
        }
    };
};
